import { useState } from 'react';

import { AddToSubsetDialog } from './add-to-subset-dialog';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button, type ButtonProps } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { cn } from '@/new/lib/utils';

interface SubsetDropdownProps {
  matterId: string;
  subsetId: string;
  docIds?: string[];
  getDocIds?: () => Promise<string[] | null>;
  disabled?: boolean;
  size?: 'sm' | 'default' | 'lg';
  variant?: ButtonProps['variant'];
  triggerText: string;
  onCreateNew: () => void;
  isLoading?: boolean;
}

export function SubsetDropdown({
  matterId,
  subsetId,
  docIds,
  getDocIds,
  disabled,
  size,
  variant,
  triggerText,
  onCreateNew,
  isLoading,
}: SubsetDropdownProps) {
  const [isAddToSubsetDialogOpen, setIsAddToSubsetDialogOpen] = useState(false);
  const [resolvedDocIds, setResolvedDocIds] = useState<string[] | null>(null);

  const handleAddToExisting = async () => {
    if (getDocIds) {
      const ids = await getDocIds();
      if (!ids) return;
      setResolvedDocIds(ids);
    }
    setIsAddToSubsetDialogOpen(true);
  };

  const isDisabled = disabled || isLoading;

  return (
    <>
      <PermissionGate permission={Permission.SubsetCreate} uiMode="disabled">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size={size} variant={variant} disabled={isDisabled}>
              <Icon className={isLoading ? 'lucide--loader animate-spin ease-in-out' : 'lucide--group'} />
              {triggerText}
              <Icon className="lucide--chevron-down" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" className={cn(size === 'sm' && 'text-xs')}>
            <DropdownMenuItem className={cn(size === 'sm' && 'text-xs')} onClick={onCreateNew}>
              <Icon className="lucide--plus" /> Create New
            </DropdownMenuItem>
            <DropdownMenuItem className={cn('pl-1.75', size === 'sm' && 'text-xs')} onClick={handleAddToExisting}>
              <Icon className="lucide--list-plus" />
              Add to Existing
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PermissionGate>

      <AddToSubsetDialog
        isOpen={isAddToSubsetDialogOpen}
        onOpenChange={(open) => {
          setIsAddToSubsetDialogOpen(open);
          if (!open) setResolvedDocIds(null);
        }}
        matterId={matterId}
        currentSubsetId={subsetId}
        docIds={resolvedDocIds ?? docIds ?? []}
      />
    </>
  );
}
